/**
 * LogsDataTable Component - Epic 38 - Story 38.1
 *
 * DataTable com multi-column sorting (Shift+Click) para logs de auditoria
 * Features:
 * - Até 3 níveis de ordenação
 * - Visual priority indicators (①②③)
 * - Click: sort único, Shift+Click: adiciona/remove sort
 * - Dark mode support
 */

import React from 'react';
import { ArrowUp, ArrowDown, Eye, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { AuditLogListItem, MultiSortConfig, SortDirection } from '../types/auditLog';
import { OperationBadge } from './OperationBadge';
import logService from '../services/logService';

interface LogsDataTableProps {
  logs: AuditLogListItem[];
  sortConfig: MultiSortConfig;
  onSortChange: (sortConfig: MultiSortConfig) => void;
  onLogClick: (log: AuditLogListItem) => void;
  // Pagination
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

export const LogsDataTable: React.FC<LogsDataTableProps> = ({
  logs,
  sortConfig,
  onSortChange,
  onLogClick,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  loading = false,
}) => {
  /**
   * Manipula clique no header da coluna
   * - Click normal: define ordenação única
   * - Shift+Click: adiciona/remove nível de ordenação (max 3)
   */
  const handleSort = (key: string, event: React.MouseEvent) => {
    if (event.shiftKey) {
      // Shift+Click: multi-column sort
      const existingIndex = sortConfig.findIndex((s) => s.key === key);

      if (existingIndex >= 0) {
        // Já existe: alterna direção ou remove
        const existing = sortConfig[existingIndex];
        const newConfig = [...sortConfig];

        if (existing.direction === 'asc') {
          // asc → desc
          newConfig[existingIndex] = { key, direction: 'desc' };
        } else {
          // desc → remove
          newConfig.splice(existingIndex, 1);
        }

        onSortChange(newConfig);
      } else {
        // Não existe: adiciona (max 3 níveis)
        if (sortConfig.length < 3) {
          onSortChange([...sortConfig, { key, direction: 'asc' }]);
        } else {
          // Já tem 3: substitui o último
          onSortChange([...sortConfig.slice(0, 2), { key, direction: 'asc' }]);
        }
      }
    } else {
      // Click normal: sort único
      const existing = sortConfig.find((s) => s.key === key);
      const newDirection: SortDirection =
        existing?.direction === 'asc' ? 'desc' : 'asc';

      onSortChange([{ key, direction: newDirection }]);
    }
  };

  /**
   * Renderiza ícone de ordenação com priority indicator
   */
  const renderSortIcon = (key: string) => {
    const sortIndex = sortConfig.findIndex((s) => s.key === key);

    if (sortIndex < 0) {
      // Não está ordenado
      return (
        <div className="flex flex-col text-gray-400">
          <ArrowUp size={12} />
          <ArrowDown size={12} className="-mt-1" />
        </div>
      );
    }

    const sort = sortConfig[sortIndex];
    const priorityLabels = ['①', '②', '③'];

    return (
      <div className="flex items-center gap-1">
        {sort.direction === 'asc' ? (
          <ArrowUp size={16} className="text-blue-600 dark:text-blue-400" />
        ) : (
          <ArrowDown size={16} className="text-blue-600 dark:text-blue-400" />
        )}
        {sortConfig.length > 1 && (
          <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
            {priorityLabels[sortIndex]}
          </span>
        )}
      </div>
    );
  };

  /**
   * Renderiza header da coluna clicável
   */
  const SortableHeader: React.FC<{ label: string; sortKey: string }> = ({
    label,
    sortKey,
  }) => (
    <th
      onClick={(e) => handleSort(sortKey, e)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none"
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
        {renderSortIcon(sortKey)}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Carregando logs...</div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
        <div className="text-gray-500 dark:text-gray-400">
          Nenhum log encontrado com os filtros aplicados
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <SortableHeader label="ID" sortKey="id" />
                <SortableHeader label="Usuário" sortKey="usuario_nome" />
                <SortableHeader label="Empresa" sortKey="empresa" />
                <SortableHeader label="Operação" sortKey="operacao" />
                <SortableHeader label="Entidade" sortKey="entidade" />
                <SortableHeader label="ID Entidade" sortKey="id_entidade" />
                <SortableHeader label="Data/Hora" sortKey="data_hora" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Detalhes
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {logs.map((log) => (
                <tr
                  key={log.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {log.id}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {log.usuario_nome || 'Sistema'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {log.empresa || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <OperationBadge operation={log.operacao} showIcon={false} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {log.entidade}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-400">
                    {log.id_entidade}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {logService.formatDateTime(log.data_hora)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                    <button
                      onClick={() => onLogClick(log)}
                      className="inline-flex items-center justify-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                      title="Ver detalhes"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="text-sm text-gray-700 dark:text-gray-300">
          Exibindo{' '}
          <span className="font-semibold">
            {(currentPage - 1) * pageSize + 1}
          </span>{' '}
          a{' '}
          <span className="font-semibold">
            {Math.min(currentPage * pageSize, totalCount)}
          </span>{' '}
          de <span className="font-semibold">{totalCount}</span> registros
        </div>

        <div className="flex items-center gap-2">
          {/* First Page */}
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsLeft size={16} />
          </button>

          {/* Previous Page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
          </button>

          {/* Page Indicator */}
          <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
            Página {currentPage} de {totalPages}
          </span>

          {/* Next Page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={16} />
          </button>

          {/* Last Page */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>

      {/* Sorting Help */}
      {sortConfig.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2 text-sm text-blue-700 dark:text-blue-300">
          💡 Dica: Use <kbd className="px-2 py-1 bg-white dark:bg-gray-800 rounded border border-blue-300 dark:border-blue-600">Shift</kbd> + Click para adicionar mais níveis de ordenação (até 3)
        </div>
      )}
    </div>
  );
};
